import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';

const CardTemplate2 = ({ cardData = {} }) => {
  const {
    name = 'Jane Smith',
    title = 'Marketing Director',
    company = 'Creative Agency',
    email = 'jane.smith@example.com',
    phone = '+1 (555) 987-6543',
    location = 'San Francisco, CA',
    bio = 'Creative marketing professional with expertise in digital campaigns and brand strategy.',
    linkedin = 'https://linkedin.com/in/janesmith',
    twitter = 'https://twitter.com/janesmith',
    instagram = 'https://instagram.com/janesmith',
    facebook = 'https://facebook.com/janesmith',
    website = 'https://janesmith.com',
    profileImage = null,
  } = cardData;

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${name} - Digital Business Card`,
        text: `Connect with ${name}`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const handleDownload = () => {
    alert('Download functionality would be implemented here');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 450,
          width: '100%',
          borderRadius: 1,
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: 'white',
            p: 4,
            textAlign: 'center',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <Avatar
            src={profileImage}
            sx={{
              width: 100,
              height: 100,
              mx: 'auto',
              mb: 2,
              border: '3px solid #e2e8f0',
            }}
          >
            {name.charAt(0)}
          </Avatar>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
            {name}
          </Typography>
          <Typography variant="h6" sx={{ color: 'primary.main', mb: 1, fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            {company}
          </Typography>
        </Box>

        {/* Content */}
        <CardContent sx={{ p: 0 }}>
          {/* Bio Section */}
          <Box sx={{ p: 3, background: '#f8fafc' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
              {bio}
            </Typography>
          </Box>

          <Divider />

          {/* Contact Information */}
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}>
              Contact Information
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EmailIcon sx={{ color: 'primary.main', mr: 2, fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Email
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500, ml: 4 }}>
                  {email}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PhoneIcon sx={{ color: 'primary.main', mr: 2, fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Phone
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500, ml: 4 }}>
                  {phone}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LocationIcon sx={{ color: 'primary.main', mr: 2, fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Location
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500, ml: 4 }}>
                  {location}
                </Typography>
              </Grid>
              
              {website && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <BusinessIcon sx={{ color: 'primary.main', mr: 2, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Website
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, ml: 4 }}>
                    {website}
                  </Typography>
                </Grid>
              )}
            </Grid>

            {/* Social Links */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                Social Media
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {linkedin && (
                  <Chip
                    icon={<LinkedInIcon />}
                    label="LinkedIn"
                    variant="outlined"
                    size="small"
                    sx={{ mb: 1 }}
                  />
                )}
                {twitter && (
                  <Chip
                    icon={<TwitterIcon />}
                    label="Twitter"
                    variant="outlined"
                    size="small"
                    sx={{ mb: 1 }}
                  />
                )}
                {instagram && (
                  <Chip
                    icon={<InstagramIcon />}
                    label="Instagram"
                    variant="outlined"
                    size="small"
                    sx={{ mb: 1 }}
                  />
                )}
                {facebook && (
                  <Chip
                    icon={<FacebookIcon />}
                    label="Facebook"
                    variant="outlined"
                    size="small"
                    sx={{ mb: 1 }}
                  />
                )}
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                startIcon={<ShareIcon />}
                onClick={handleShare}
                sx={{
                  borderRadius: 1,
                  px: 3,
                  py: 1.5,
                }}
              >
                Share Card
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                sx={{ borderRadius: 1, px: 3, py: 1.5 }}
              >
                Save Contact
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CardTemplate2;






